import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { Swiper, Navigation, Thumbs, Pagination } from '@theme/vendor';
import { isMobileBreakpoint, isTabletBreakpoint, getStickyOffset, debounce } from '@theme/utilities';
import { morph } from '@theme/morph';
import { syncDeferredMedia } from '@theme/media';

const safeParseJson = (value, fallback = {}) => {
  if (!value) return fallback;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

/**
 * A custom element that renders a media gallery.
 * @extends Component
 */
export class MediaGallery extends Component {
  #prevWidth = window.innerWidth;

  /** True until the first `change` after each lightbox open — that event is init, not navigation. */
  #lightboxSkipFirstGalleryChange = false;

  #clickScrollTimer = null;
  #isClickScrolling = false;
  #onClickScrollEnd = null;

  #galleryScrollRaf = 0;
  #currentActiveIndex = -1;
  #cachedSlides = null;
  #cachedThumbItems = null;
  #cachedThumbContainer = null;
  /** @type {Element|Window|null} */
  #scrollContainer = null;
  #scrollContainerPaddingTop = 0;

  #lightboxClickHandlersAdded = false;
  #lightboxGalleryItems;
  #lastClickedItem;
  #thumbnailsClickHandler;
  #preventClickHandler;
  #boundOnVariantSelected;
  #variantUpdateTarget;
  #mediaStartedHandler;

  #gallerySwiperEl = null;
  #thumbnailsSwiperEl = null;

  /** @type {IntersectionObserver|null} */
  #gridAutoplayObserver = null;

  #isMobileViewport = isMobileBreakpoint();

  #teardownGridAutoplay() {
    this.#gridAutoplayObserver?.disconnect();
    this.#gridAutoplayObserver = null;
  }

  #disconnectAllMediaObservers() {
    this.#teardownGridAutoplay();
  }

  #handleResize = debounce(() => {
    const width = window.innerWidth;
    const widthChanged = width !== this.#prevWidth;
    this.#prevWidth = width;

    // Only react to width changes (not iOS chrome bar height changes)
    if (!widthChanged) return;

    this.initGalleryCarousel();

    requestAnimationFrame(() => {
      const nextIsMobile = isMobileBreakpoint();
      if (nextIsMobile !== this.#isMobileViewport) {
        this.#isMobileViewport = nextIsMobile;
        this.initializeZoom();
      }
    });
  }, 50);

  /** @param {KeyboardEvent} e */
  #paginationKeydownHandler = (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    if (!e.target?.classList?.contains('swiper-pagination-bullet')) return;
    if (!this.contains(/** @type {Node} */ (e.target))) return;
    e.preventDefault();
    /** @type {HTMLElement} */ (e.target).click();
  };

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('resize', this.#handleResize);

    this.#initialize();

    this.#variantUpdateTarget = this.closest('.shopify-section, .dialog');
    if (this.#variantUpdateTarget) {
      this.#boundOnVariantSelected = (event) => this.onVariantSelected(event);
      this.#variantUpdateTarget.addEventListener(ThemeEvents.variantUpdate, this.#boundOnVariantSelected);
    }
  }

  /**
   * Initializes components that depend on DOM structure.
   */
  #initialize() {
    this.#gallerySwiperEl = this.querySelector('.media-gallery__carousel');
    this.#thumbnailsSwiperEl = this.querySelector('.media-thumbnails__carousel');

    this.initGalleryCarousel();
    this.loadMediaGridOnScroll();

    this.#isMobileViewport = isMobileBreakpoint();
    this.initializeZoom();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.removeEventListener('resize', this.#handleResize);
    this.#handleResize.cancel?.();

    if (this.#variantUpdateTarget && this.#boundOnVariantSelected) {
      this.#variantUpdateTarget.removeEventListener(ThemeEvents.variantUpdate, this.#boundOnVariantSelected);
      this.#boundOnVariantSelected = null;
    }
    this.#variantUpdateTarget = null;

    this.#disconnectAllMediaObservers();
    this.#teardownGalleryScrollObserver();
    this.cleanupZoom();

    this.#gallerySwiperEl = null;
    this.#thumbnailsSwiperEl = null;
  }

  /**
   * Initializes gallery based on current breakpoint (mobile or desktop).
   */
  #initializeGalleryByBreakpoint() {
    isMobileBreakpoint() ? this.initializeGalleryMobile() : this.initializeGalleryDesktop();
  }

  /**
   * Initializes the gallery carousel based on device type.
   * In editor: uses setTimeout so iframe layout is applied before reading breakpoint.
   */
  initGalleryCarousel() {
    if (Shopify.designMode) {
      setTimeout(() => this.#initializeGalleryByBreakpoint(), 200);
    } else {
      requestAnimationFrame(() => this.#initializeGalleryByBreakpoint());
    }
  }

  /**
   * Initializes the gallery components with proper synchronization.
   */
  async initializeGallery() {
    await this.initThumbnailsCarousel();
    this.initGallerySlider();
  }

  /**
   * Initializes gallery for desktop devices.
   * Destroys existing sliders if not needed, then initializes new ones.
   */
  initializeGalleryDesktop() {
    if (!this.isGallerySlider) {
      this.destroyGallerySlider();
      if (this.isStackedWithThumbnails) {
        this.#setupGalleryScrollObserver();
      } else {
        this.destroyThumbnailsSlider();
        this.#teardownGalleryScrollObserver();
      }
      return;
    }
    this.initializeGallery();
  }


  /**
   * Initializes gallery for mobile devices.
   * Handles different mobile thumbnail layouts (thumbnails or slider).
   */
  initializeGalleryMobile() {
    this.#teardownGalleryScrollObserver();
    if (this.galleryLayoutMobile === 'thumbnails') {
      this.initializeGallery();
    } else if (this.galleryLayoutMobile === 'slider') {
      this.initGallerySlider();
    } else if (this.galleryLayoutMobile === 'grid') {
      this.initGalleryGridMobile();
    }
  }

  /**
   * Destroys the thumbnails slider instance to prevent memory leaks.
   * Safely destroys Swiper instance and resets the reference.
   */
  destroyThumbnailsSlider() {
    if (this.#thumbnailsClickHandler) {
      this.thumbnailsSwiper?.removeEventListener('click', this.#thumbnailsClickHandler);
      this.#thumbnailsClickHandler = null;
    }
    if (this.thumbnailsSwiper?.swiper && typeof this.thumbnailsSwiper.swiper.destroy === 'function') {
      this.thumbnailsSwiper.swiper.destroy(true, true);
      this.thumbnailsSwiper.swiper = false;
    }
  }

  /**
   * Destroys the main gallery slider instance to prevent memory leaks.
   * Safely destroys Swiper instance and resets the reference.
   */
  destroyGallerySlider() {
    const gallery = this.gallerySwiper;

    if (this.#mediaStartedHandler) {
      gallery?.removeEventListener(ThemeEvents.mediaStartedPlayed, this.#mediaStartedHandler);
      this.#mediaStartedHandler = null;
    }


    if (gallery?.querySelector('.swiper-pagination')) {
      this.removeEventListener('keydown', this.#paginationKeydownHandler);
    }

    if (gallery?.swiper && typeof gallery.swiper.destroy === 'function') {
      gallery.swiper.destroy(true, true);
      gallery.swiper = false;
    }
  }

  /**
   * Initializes the thumbnails component.
   * @returns {Promise<MediaThumbnailsCarousel|null>} The thumbnails carousel instance
   */
  initThumbnailsCarousel() {
    const thumbnailsSwiper = this.thumbnailsSwiper;
    if (!thumbnailsSwiper) return;
    this.destroyThumbnailsSlider();
    const [slider, sliderWrapper] = [thumbnailsSwiper.querySelector('.swiper-thumbnails'), thumbnailsSwiper.querySelector('.swiper-thumbnails-wrapper')];
    if ( !slider || !sliderWrapper) return;

    slider.classList.add('swiper');
    sliderWrapper.classList.add('swiper-wrapper');

    let options = safeParseJson(thumbnailsSwiper.getAttribute('data-options'));
    if (isMobileBreakpoint()) {
      options = safeParseJson(thumbnailsSwiper.getAttribute('data-options-mobile'));
    } else if (isTabletBreakpoint() && this.galleryLayoutDesktop === 'left_thumbnails') {
      options = safeParseJson(thumbnailsSwiper.getAttribute('data-options-tablet'));
    }

    thumbnailsSwiper.swiper = new Swiper(slider, options);
    this.#addSlideThumbnailsClickListeners();

    return thumbnailsSwiper.swiper;
  }

  /**
   * Add click event listeners to thumbnail slides
   */
  #addSlideThumbnailsClickListeners() {
    const swiper = this.thumbnailsSwiper;
    if (!swiper) return;
    this.#thumbnailsClickHandler = (e) => {
      const slide = e.target.closest('.swiper-slide');
      if (!slide) return;
      const index = [...(slide.parentElement?.children ?? [])].indexOf(slide);
      this.#handleSlideThumbnailsClick(index, e);
    };
    swiper.addEventListener('click', this.#thumbnailsClickHandler);
  }

  /**
   * Handle thumbnail slide click
   * @param {number} index - The slide index
   * @param {Event} event - The click event
   */
  #handleSlideThumbnailsClick(index, event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.thumbnailsSwiper?.swiper) {
      const centeredIndex = index <= 1 ? 0 : Math.max(0, index - 2);
      this.thumbnailsSwiper.swiper.slideTo(centeredIndex, 300, false);
    }
  }

  onThumbnailItemClick(event) {
    const item = event.target.closest('.media-thumbnails__item');
    if (!item) return;
    const index = parseInt(item.dataset.index ?? '-1', 10);
    if (index < 0) return;
    if (item.classList.contains('is-active')) return;

    this.#syncThumbnailToIndex(index);

    const targetSlide = this.gallerySwiper?.querySelectorAll('.swiper-slide')[index];
    if (!targetSlide) return;

    const scrollEl = this.#scrollContainer;
    const slideRect = targetSlide.getBoundingClientRect();

    this.#isClickScrolling = true;
    clearTimeout(this.#clickScrollTimer);

    let targetScrollY;
    if (scrollEl && scrollEl !== window) {
      // Dialog context: scroll the dialog__inner container
      const el = /** @type {Element} */ (scrollEl);
      const containerRect = el.getBoundingClientRect();
      targetScrollY = Math.max(0, el.scrollTop + slideRect.top - containerRect.top - this.#scrollContainerPaddingTop);

      if (this.#onClickScrollEnd) {
        scrollEl.removeEventListener('scrollend', this.#onClickScrollEnd);
      }
      this.#onClickScrollEnd = () => {
        clearTimeout(this.#clickScrollTimer);
        this.#isClickScrolling = false;
        this.#onClickScrollEnd = null;
      };
      this.#clickScrollTimer = setTimeout(this.#onClickScrollEnd, 2000);
      scrollEl.addEventListener('scrollend', this.#onClickScrollEnd, { once: true });
      /** @type {Element} */ (scrollEl).scrollTo({ top: targetScrollY, behavior: 'smooth' });
    } else {
      // Page context: scroll window with sticky header offset
      const currentStickyOffset = getStickyOffset();
      const scrollingUp = slideRect.top < currentStickyOffset + 20;
      const offset = getStickyOffset({ scrollingUp });
      targetScrollY = Math.max(0, slideRect.top + window.scrollY - offset - 20);

      // Lock thumbnail top before scroll starts to prevent --header-sticky-content
      // from changing mid-animation (header-pinning fires when scrolling up/down)
      const container = this.#cachedThumbContainer;
      if (container) container.style.top = `calc(${offset}px + 2rem)`;

      if (this.#onClickScrollEnd) {
        window.removeEventListener('scrollend', this.#onClickScrollEnd);
      }
      this.#onClickScrollEnd = () => {
        clearTimeout(this.#clickScrollTimer);
        this.#isClickScrolling = false;
        this.#onClickScrollEnd = null;
        if (container) container.style.top = '';
      };
      // Fallback for browsers without scrollend support (Safari < 17.4)
      this.#clickScrollTimer = setTimeout(this.#onClickScrollEnd, 2000);
      window.addEventListener('scrollend', this.#onClickScrollEnd, { once: true });
      window.scrollTo({ top: targetScrollY, behavior: 'smooth' });
    }
  }

  /**
   * Initializes the main gallery slider with Swiper.js.
   * Sets up navigation, thumbs synchronization, mobile options, and event listeners.
   * Also updates the gallery to show the default variant image.
   */
  initGallerySlider() {
    const gallerySwiper = this.gallerySwiper;
    if (!gallerySwiper) return;

    const isMobile = isMobileBreakpoint();

    this.destroyGallerySlider();

    if (isMobile && this.galleryLayoutMobile === 'grid') {
      return;
    }
    const [slider, sliderWrapper] = [gallerySwiper.querySelector('.swiper-gallery'), gallerySwiper.querySelector('.swiper-gallery-wrapper')];
    if (!slider || !sliderWrapper) return;

    slider.classList.add('swiper');
    sliderWrapper.classList.add('swiper-wrapper');

    let options = safeParseJson(gallerySwiper.getAttribute('data-options'));
    options.modules = [Navigation, Thumbs, Pagination];
    if (this.thumbnailsSwiper?.swiper) {
      options.thumbs = { swiper: this.thumbnailsSwiper.swiper };
    }
    options.autoHeight = true;
    if (gallerySwiper.getAttribute('data-next-slide-mobile') === 'true' && isMobile) {
      options.slidesPerView = 'auto';
      options.autoHeight = false;
    }

    if (this.galleryLayoutDesktop === 'horizontal' && !isMobile) {
      options.slidesPerView = 'auto';
      options.autoHeight = false;
    }

    if (gallerySwiper.querySelector('.swiper-navigation')) {
      options.navigation = {
        nextEl: gallerySwiper.querySelector('.swiper-btn--next'),
        prevEl: gallerySwiper.querySelector('.swiper-btn--prev'),
      };
    }

    const pagination = gallerySwiper.querySelector('.swiper-pagination');
    if (pagination) {
      options.pagination = {
        el: pagination,
        clickable: true,
        type: pagination.getAttribute('data-pagination-type')
      };
    }

    gallerySwiper.swiper = new Swiper(slider, options);


    gallerySwiper.swiper.on('slideChangeTransitionEnd', () => {
      this.syncMediaPlayback(gallerySwiper.swiper);
      this.handleModelViewerSlide(gallerySwiper.swiper);
    });

    gallerySwiper.swiper.on('touchStart', this.onGalleryTouchStart);
    gallerySwiper.swiper.on('touchEnd', this.onGalleryTouchEnd);

    if (this.#mediaStartedHandler) {
      gallerySwiper.removeEventListener(ThemeEvents.mediaStartedPlayed, this.#mediaStartedHandler);
    }

    this.#mediaStartedHandler = () => this.handleModelViewerSlide(gallerySwiper.swiper);
    gallerySwiper.addEventListener(ThemeEvents.mediaStartedPlayed, this.#mediaStartedHandler);

    this.updateGalleryToDefaultVariantImage();

    if (gallerySwiper.querySelector('.swiper-pagination')) {
      this.addEventListener('keydown', this.#paginationKeydownHandler);
    }
  }

  /**
   * Initializes the gallery grid component.
   */

  async initGalleryGridMobile() {
    this.destroyGallerySlider();
    this.destroyThumbnailsSlider();
    this.querySelector('.swiper-gallery')?.classList.add('swipe-mobile');
    this.querySelector('.swiper-gallery-wrapper')?.classList.add('swipe-mobile__inner');

    await customElements.whenDefined('deferred-media');
    this.loadMediaGridOnScrollMobile();
  }

  /**
   * Grid single-active autoplay: only the most-visible media item plays, the rest pause —
   * consistent scrolling up or down, and resumes on scroll-back.
   */
  #setupGridAutoplay() {
    this.#teardownGridAutoplay();
    const items = this.querySelectorAll('deferred-media');
    if (!items.length) return;

    const visibleArea = new Map();
    let activeItem = null;

    this.#gridAutoplayObserver = new IntersectionObserver((entries) => {
      // Rank by intersection area (works for both vertical and horizontal layouts).
      for (const entry of entries) {
        const { width, height } = entry.intersectionRect;
        visibleArea.set(entry.target, width * height);
      }

      let next = null;
      let maxArea = 0;
      for (const [item, area] of visibleArea) {
        if (area > maxArea) {
          maxArea = area;
          next = item;
        }
      }

      if (next === activeItem) return;
      activeItem = next;

      for (const item of items) {
        if (item !== activeItem) item.pauseMedia?.();
        else if (item.hasAttribute('data-media-loaded')) item.playMedia?.();
        else item.loadContent?.(false, true);
      }
    }, { threshold: [0, 0.25, 0.5, 0.75, 1] });

    items.forEach((item) => this.#gridAutoplayObserver.observe(item));
  }

  /**
   * Sets up intersection observer to load media grid items on scroll for mobile grid layout.
   * Only works for mobile grid layout with video autoplay enabled.
   */
  loadMediaGridOnScrollMobile() {
    if (this.isVideoAutoplay) this.#setupGridAutoplay();
    else this.#teardownGridAutoplay();
  }

  /**
   * Handles model viewer slide changes
   * Hides navigation and disables mousewheel when model viewer is active
   * Prevents conflicts between 3D model controls and carousel controls
   * @param {Swiper} swiper - The swiper instance
   */
  handleModelViewerSlide(swiper) {
    if (!swiper?.slides || swiper.activeIndex === undefined) return;

    const currentSlide = swiper.slides[swiper.activeIndex];
    if (!currentSlide) return;

    const isModelViewer = currentSlide.querySelector('model-viewer');

    // Hide navigation when model viewer is active (user needs to interact with model)
    const navigation = this.gallerySwiper.querySelector('.swiper-navigation');
    if (navigation) {
      navigation.classList.toggle('!hidden', isModelViewer);
    }

    // Disable mousewheel to prevent carousel scrolling while rotating model
    if (swiper.mousewheel) {
      swiper.mousewheel.enabled = !isModelViewer;
    }
  }

  /**
   * Handles touch start on gallery
   * Disables carousel swipe if touch starts on model-viewer (allows model rotation)
   */
  onGalleryTouchStart = (_, e) => {
    const target = e?.target || e;
    const startedOnModel = !!target?.closest?.('model-viewer');
    // Disable carousel swipe when interacting with 3D model
    if (this.gallerySwiper?.swiper) {
      this.gallerySwiper.swiper.allowTouchMove = !startedOnModel;
    }
  };

  /**
   * Re-enables carousel swipe after touch ends
   */
  onGalleryTouchEnd = () => {
    if (this.gallerySwiper?.swiper) {
      this.gallerySwiper.swiper.allowTouchMove = true;
    }
  };

  /**
   * Syncs media playback state between active and inactive slides
   * Plays media on active slide, pauses on inactive slides
   * Handles lazy loading for autoplay videos
   * @param {Swiper} swiper - The swiper instance
   */
  syncMediaPlayback(swiper) {
    if (!swiper?.el || swiper.activeIndex === undefined) return;
    if(this.galleryLayoutDesktop === 'horizontal' && ! isMobileBreakpoint()) return;

    const allDeferredMedia = swiper.el.querySelectorAll('deferred-media');
    const videoAutoplay = this.isVideoAutoplay;
    const activeSlide = swiper.slides[swiper.activeIndex];

    allDeferredMedia.forEach(media => {
      const slide = media.closest('.swiper-slide');
      if (!slide) return;

      syncDeferredMedia(media, {
        isActive: slide === activeSlide,
        shouldAutoplay: videoAutoplay,
        resumeNonAutoplay: videoAutoplay,
      });
    });
  }

  /**
   * Sets up intersection observers to load deferred-media as items enter the viewport.
   * Skips mobile carousels and desktop carousels unless layout is horizontal (see `#shouldLoadDeferredMediaViaGridScroll`).
   * Requires video autoplay to be enabled.
   */
  loadMediaGridOnScroll() {
    if (this.#shouldLoadDeferredMediaViaGridScroll() && this.isVideoAutoplay) this.#setupGridAutoplay();
    else this.#teardownGridAutoplay();
  }

  /**
   * Whether `loadMediaGridOnScroll` should attach intersection observers on deferred-media.
   * Carousels are driven by Swiper slide changes (not scroll-in-view), except horizontal desktop
   * strip where thumbnails still behave like an in-flow grid for observers.
   */
  #shouldLoadDeferredMediaViaGridScroll() {
    if (!this.isCarousel) return true;
    if (isMobileBreakpoint()) return false;
    return this.galleryLayoutDesktop === 'horizontal';
  }

  /**
   * Cleans up existing zoom and lightbox instances.
   * Should be called before reinitializing after DOM changes.
   */
  cleanupZoom() {
    if (this.#preventClickHandler) {
      this.gallerySwiper?.removeEventListener('click', this.#preventClickHandler);
      this.#preventClickHandler = null;
    }

    if (this.lightbox) {
      if (this.lightbox.pswp && !this.lightbox.pswp.isClosed) {
        this.lightbox.pswp.close();
      }
      if (typeof this.lightbox.destroy === 'function') {
        this.lightbox.destroy();
      }
      this.lightbox = null;
      this.#lightboxGalleryItems = null;
    }
    this.#lightboxClickHandlersAdded = false;

    if (this.magnify) {
      if (typeof this.magnify.destroy === 'function') {
        this.magnify.destroy();
      }
      this.magnify = null;
    }
  }

  /**
   * Initializes zoom functionality based on configuration.
   * Sets up either hover zoom or lightbox based on data attributes.
   */
  initializeZoom() {
    this.cleanupZoom();
    const zoom = this.#isMobileViewport ? this.zoomMobile : this.zoom;
    if (zoom === 'zoom_on_hover') {
      this.initZoomOnHover();
    } else if (zoom === 'lightbox') {
      this.initLightbox();
    } else {
      this.preventSlideLinkClicks();
    }
  }

  /**
   * Prevents default click behavior on gallery slide links.
   * Used when zoom or lightbox functionality is not enabled.
   */
  preventSlideLinkClicks() {
    const gallery = this.gallerySwiper;
    if (!gallery || this.#preventClickHandler) return;
    this.#preventClickHandler = (e) => {
      if (e.target.closest('.swiper-slide-link')) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    gallery.addEventListener('click', this.#preventClickHandler);
  }

  /**
   * Initializes hover zoom functionality using LuTheme ImageMagnify.
   * Creates magnify instance for zoom-on-hover images.
   */
   initZoomOnHover() {
    if(isMobileBreakpoint()) return;
    this.preventSlideLinkClicks();
    if (this.magnify || typeof window.LuTheme?.ImageMagnify === 'undefined') return;

    this.magnify = new window.LuTheme.ImageMagnify({
      zoomRatio: 3,
      selectors: this.gallerySwiper.querySelectorAll('.swiper-slide--zoom-image img')
    });
  }

  /**
   * Initialize PhotoSwipe lightbox
   */
  #createThumbnailUrl(originalUrl) {
    if (!originalUrl) return '';
    const url = new URL(originalUrl, window.location.origin);
    url.searchParams.set('width', '80');
    return url.toString();
  }

  initLightbox() {
    if (this.lightbox) return;

    const galleryItems = Array.from(this.querySelectorAll('.swiper-slide--lightbox'));

    // Always attach click handlers so navigation is prevented even if PhotoSwipe
    // hasn't loaded yet. The handlers lazily call initLightbox() on first click.
    if (!this.#lightboxClickHandlersAdded) {
      this.#lightboxClickHandlersAdded = true;
      this.addClickHandlers(galleryItems);
    }

    if (typeof window.LuTheme?.PhotoSwipeLightbox === 'undefined') return;
    const dataSource = galleryItems.map((item, index) => {
      const mediaType = item.dataset.mediaType || 'image';

      switch (mediaType) {
        case 'model': {
          const modelTemplate = item.querySelector('product-model');
          const modelHTML = modelTemplate ? modelTemplate.outerHTML.replace(/sizes="[^"]*"/g, 'sizes="100vw"') : '';
          return {
            id: index,
            html: '<div class="pswp__item--model">' + modelHTML + '</div>',
            mediaType,
            msrc: this.#createThumbnailUrl(item.dataset.src)
          };
        }
        case 'video':
        case 'external_video': {
          const videoTemplate = item.querySelector('deferred-media');
          const videoHTML = videoTemplate ? videoTemplate.outerHTML.replace(/sizes="[^"]*"/g, 'sizes="100vw"') : '';
          return {
            id: index,
            html: '<div class="pswp__item--video">' + videoHTML + '</div>',
            mediaType,
            msrc: this.#createThumbnailUrl(item.dataset.src)
          };
        }
        default:
          return {
            id: index,
            src: item.dataset.src,
            width: parseInt(item.dataset.pswpWidth, 10) || 800,
            height: parseInt(item.dataset.pswpHeight, 10) || 600,
            mediaType,
            msrc: this.#createThumbnailUrl(item.dataset.src)
          };
      }
    });

    this.#lightboxGalleryItems = galleryItems;

    this.lightbox = new window.LuTheme.PhotoSwipeLightbox({
      dataSource,
      pswpModule: window.LuTheme.PhotoSwipe,
      arrowPrevSVG: '<svg width="24" height="25" viewBox="0 0 24 25" fill="none"><path d="M15 18.5L9 12.5L15 6.5" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"></path></svg>',
      arrowNextSVG: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"></path></svg>',
      closeSVG: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M19 5L5 19" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"></path><path d="M5 5L19 19" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"></path></svg>',
      bgOpacity: 1,
      zoom: false,
      counter: false,
      returnFocus: false,
      showHideAnimationType: 'zoom',
      showAnimationDuration: 300,
      hideAnimationDuration: 300,
      easing: 'cubic-bezier(0.4, 0, 0.22, 1)',
      allowPanToNext: true,
      allowMouseDrag: true,
      closeOnVerticalDrag: true,
      closeOnScroll: false,
      getViewportSizeFn: () => ({
        x: document.documentElement.clientWidth,
        y: isMobileBreakpoint() ? window.innerHeight - 83 : window.innerHeight - 108
      })
    });

    this.setupLightboxFilters();
    this.setupLightboxEvents(galleryItems);
    this.lightbox.init();
  }

  setupLightboxFilters() {
    this.lightbox.addFilter("thumbEl", (thumbEl, itemData, index) => {
      const items = this.#lightboxGalleryItems;
      const itemIndex = typeof index === 'number' ? index : itemData?.id;
      if (items?.length && itemIndex !== undefined && items[itemIndex]) {
        const img = items[itemIndex].querySelector('img');
        if (img) return img;
      }
      if (this.#lastClickedItem) {
        const img = this.#lastClickedItem.querySelector('img');
        if (img) return img;
      }
      return thumbEl;
    });

    this.lightbox.addFilter("placeholderSrc", (placeholderSrc, { data }) => {
      const items = this.#lightboxGalleryItems;
      const id = data?.id;
      if (items?.length && id !== undefined && items[id]) {
        const img = items[id].querySelector('img');
        if (img?.src) return img.src;
      }
      if (this.#lastClickedItem) {
        const img = this.#lastClickedItem.querySelector('img');
        if (img?.src) return img.src;
      }
      return placeholderSrc;
    });

  }

  setupLightboxEvents(galleryItems) {
    this.lightbox.on('beforeOpen', () => {
      this.#lightboxSkipFirstGalleryChange = true;
    });

    this.lightbox.on("change", () => {
      if (this.#lightboxSkipFirstGalleryChange) {
        this.#lightboxSkipFirstGalleryChange = false;
        if (this.gallerySwiper?.swiper) {
          this.manageVideoPlaybackInLightbox();
        }
        return;
      }

      if (this.gallerySwiper.swiper) {
        const { currIndex } = this.lightbox.pswp;
        if (this.gallerySwiper?.swiper && this.gallerySwiper.swiper.realIndex !== currIndex) {
          if (typeof this.gallerySwiper.swiper.slideToLoop === 'function') {
            this.gallerySwiper.swiper.slideToLoop(currIndex, 100, false);
          }
        }
        this.manageVideoPlaybackInLightbox();
      } else {
        const { currIndex } = this.lightbox.pswp;
        const galleryItem = galleryItems[currIndex];
        if (galleryItem) {
          this.#lastClickedItem = galleryItem;

          const shouldScrollToItem = !isMobileBreakpoint() ||
                                    (isMobileBreakpoint() && this.galleryLayoutMobile === 'grid');

          if (shouldScrollToItem) {
            galleryItem.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'center'
            });
          }
        }
      }
    });

    this.lightbox.on("pointerDown", (e) => {
      const currentSlide = this.lightbox.pswp.currSlide;
      if (currentSlide.data.mediaType !== "image") {
        e.preventDefault();
      }
    });

    this.lightbox.on('uiRegister', () => {
      this.lightbox.pswp.ui.registerElement({
        name: 'thumbnailsIndicator',
        className: 'pswp__thumbnails',
        appendTo: 'wrapper',
        onInit: (el, pswp) => {
          const thumbs = [];
          let prevIndex = -1;
          const thumbnailsContainer = document.createElement('div');
          thumbnailsContainer.className = 'pswp__thumbnails-container';
          el.appendChild(thumbnailsContainer);

          for (let i = 0; i < pswp.getNumItems(); i++) {
            const item = pswp.getItemData(i);
            const thumbWrapper = this.createThumbnailItem(item, i, pswp);
            thumbnailsContainer.appendChild(thumbWrapper);
            thumbs.push(thumbWrapper);
          }

          pswp.on('change', () => {
            if (prevIndex >= 0) {
              thumbs[prevIndex].classList.remove('pswp__thumbnail--active');
            }
            thumbs[pswp.currIndex].classList.add('pswp__thumbnail--active');
            prevIndex = pswp.currIndex;
            this.scrollToActiveThumbnail(thumbnailsContainer, thumbs, pswp.currIndex);
          });

          pswp.on('firstUpdate', () => {
            const activeThumb = thumbs[pswp.currIndex];
            if (activeThumb) {
              this.scrollToActiveThumbnail(thumbnailsContainer, thumbs, pswp.currIndex);
            }
          });
        }
      });
    });
  }

  createThumbnailItem(item, index, pswp) {
    const thumbWrapper = document.createElement('div');
    thumbWrapper.className = 'pswp__thumbnail-item';

    const thumb = document.createElement('img');
    thumb.className = 'pswp__thumbnail';
    thumb.src = item.msrc || item.src;
    thumb.alt = item.alt || '';
    thumb.addEventListener('click', () => pswp.goTo(index));

    thumbWrapper.appendChild(thumb);

    if (item.mediaType === 'video' || item.mediaType === 'external_video') {
      thumbWrapper.appendChild(this.createMediaIcon('video'));
    } else if (item.mediaType === 'model') {
      thumbWrapper.appendChild(this.createMediaIcon('model'));
    }

    return thumbWrapper;
  }

  createMediaIcon(type) {
    const icon = document.createElement('div');
    icon.className = 'pswp__thumbnail-icon deferred-media__poster-icon';
    const svgMarkup = MediaGallery.#MEDIA_ICON_SVG[type];
    if (svgMarkup) icon.innerHTML = svgMarkup;
    return icon;
  }

  static #MEDIA_ICON_SVG = {
    video: '<span class="svg-icon"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M5.64062 2.34179C5.88136 2.21036 6.17452 2.2209 6.40527 2.36914L20.4053 11.3691C20.6199 11.5071 20.75 11.7448 20.75 12C20.75 12.2552 20.6199 12.4929 20.4053 12.6309L6.40527 21.6309C6.17452 21.7791 5.88136 21.7896 5.64062 21.6582C5.39983 21.5267 5.25 21.2743 5.25 21V2.99999L5.25684 2.89843C5.28877 2.66448 5.4299 2.45685 5.64062 2.34179Z" fill="currentColor"></path></svg></span>',
    model: '<span class="svg-icon"><svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.67998 20.629L1.28002 16.723C0.886205 16.4784 0.561675 16.1368 0.337572 15.731C0.113468 15.3251 -0.00274623 14.8686 -1.39464e-05 14.405V6.59497C-0.00238367 6.13167 0.113819 5.6755 0.33751 5.26978C0.561202 4.86405 0.884959 4.52227 1.278 4.27698L7.67796 0.377014C8.07524 0.131403 8.53292 0.000877102 8.99999 9.73346e-08C9.46678 -0.000129605 9.92446 0.129369 10.322 0.374024V0.374024L16.722 4.27399C17.1163 4.51985 17.4409 4.86287 17.6647 5.27014C17.8885 5.67742 18.0039 6.13529 18 6.59998V14.409C18.0026 14.8725 17.8864 15.3289 17.6625 15.7347C17.4386 16.1405 17.1145 16.4821 16.721 16.727L10.321 20.633C9.92264 20.8742 9.46565 21.0012 8.99999 21C8.53428 20.9998 8.07761 20.8714 7.67998 20.629V20.629ZM8.72398 2.078L2.32396 5.97803C2.22303 6.04453 2.14066 6.13551 2.08452 6.24255C2.02838 6.34959 2.00031 6.46919 2.00298 6.59003V14.4C2.00026 14.5205 2.02818 14.6396 2.08415 14.7463C2.14013 14.853 2.22233 14.9438 2.32298 15.01L7.99999 18.48V10.919C8.00113 10.5997 8.08851 10.2867 8.25292 10.0129C8.41732 9.73922 8.65267 9.51501 8.93401 9.36401L15.446 5.841L9.28001 2.08002C9.19614 2.02738 9.09901 1.99962 8.99999 2C8.90251 1.99972 8.8069 2.02674 8.72398 2.078V2.078Z" fill="currentColor"></path></svg></span>',
  };

  /**
   * Adds click event handlers to gallery items for lightbox functionality.
   * Handles image clicks to open them in the lightbox.
   * @param {Array} galleryItems - Array of gallery item elements
   */
  addClickHandlers(galleryItems) {
    galleryItems.forEach((item, index) => {
      item.addEventListener('click', (e) => {
        const zoom = this.#isMobileViewport ? this.zoomMobile : this.zoom;
        if (zoom !== 'lightbox') return;

        if (item.dataset.mediaType === 'image') {
          e.preventDefault();
          const clickedEl = e.target.closest('.swiper-slide--zoom-image') || e.target;
          this.#lastClickedItem = clickedEl;
          if (!this.lightbox) this.initLightbox();
          this.lightbox?.loadAndOpen(index);
        }
      });
    });
  }

  /**
   * Manages video autoplay and pause behavior in lightbox.
   * Pauses all videos and plays only the current active slide's video.
   * Respects video autoplay settings from configuration.
   */
  manageVideoPlaybackInLightbox() {
    if (!this.lightbox?.pswp?.element) return;
    if (this.galleryLayoutDesktop === 'horizontal' && !isMobileBreakpoint()) return;

    const { element: pswpElement, currSlide } = this.lightbox.pswp;
    const videoAutoplay = this.isVideoAutoplay;
    const allDeferredMedia = pswpElement.querySelectorAll('deferred-media');

    if (!allDeferredMedia.length) return;

    // Pause all videos first
    allDeferredMedia.forEach((media) => {
      if (media.hasAttribute('data-media-loaded')) {
        media.pauseMedia();
      }
    });

    // Handle current active slide
    if (currSlide?.isActive) {
      const currentMedia = currSlide.holderElement?.querySelector('deferred-media');
      if (currentMedia) {
        const isLoaded = currentMedia.hasAttribute('data-media-loaded');
        if (!isLoaded && videoAutoplay) {
          currentMedia.loadContent(false, true);
        } else if (isLoaded && videoAutoplay) {
          currentMedia.playMedia();
        }
      }
    }
  }

  /**
   * Scrolls the thumbnail container to center the active thumbnail.
   * Provides smooth scrolling animation for better user experience.
   * @param {HTMLElement} container - Thumbnails container
   * @param {Array} thumbs - Thumbnail elements
   * @param {number} activeIndex - Active thumbnail index
   */
  scrollToActiveThumbnail(container, thumbs, activeIndex) {
    if (!thumbs[activeIndex]) return;

    const activeThumb = thumbs[activeIndex];
    const containerRect = container.getBoundingClientRect();
    const thumbRect = activeThumb.getBoundingClientRect();

    const scrollLeft = activeThumb.offsetLeft - (containerRect.width / 2) + (thumbRect.width / 2);

    container.scrollTo({
      left: Math.max(0, scrollLeft),
      behavior: 'smooth'
    });
  }

  /**
   * Updates the gallery to show the default image of the currently selected variant.
   * Called during initialization to ensure the correct variant image is displayed.
   */
  updateGalleryToDefaultVariantImage() {
    const selectedImageId = this.variantPicker?.selectedOption?.dataset?.optionMediaId;
    if (selectedImageId) {
      this.slideToMediaById(selectedImageId);
    }
  }

  /**
   * Appends ModelViewer assets (style and script) if they don't already exist
   * @param {Document} html - HTML document containing the assets
   */
  #appendModelViewerAssets(html) {
    const productInformation = this.closest('.product-information');
    if (!productInformation) return;

    const modelViewerStyle = html.querySelector('#ModelViewerStyle');
    if (modelViewerStyle) {
      const existingStyle = productInformation.querySelector('#ModelViewerStyle');
      if (!existingStyle) {
        this.insertAdjacentElement('afterend', modelViewerStyle);
      }
    }

    const productModelScript = html.querySelector('#ProductModelScript');
    if (productModelScript) {
      const existingScript = productInformation.querySelector('#ProductModelScript');
      if (!existingScript) {
        this.insertAdjacentElement('afterend', productModelScript);
      }
    }
  }

  /**
   * Determines whether variant change can be handled by sliding to an existing media item.
   * We only use slideTo when all media already exists in current DOM.
   *
   * Horizontal galleries intentionally skip slideTo and go through morph/update flow,
   * because their variant change can require rebuilding gallery structure/layout.
   *
   * @param {string | undefined} selectedImageId - Media id from selected variant option
   * @returns {boolean} True when slideTo is safe, false when full gallery update is required
   */
  #shouldSlideToVariantMedia(selectedImageId) {
    if (!selectedImageId) return false;
    if (!this.gallerySwiper?.swiper) return false;
    if (this.selectedVariantMediaOnly) return false;
    if (this.isVariantGallery) return false;
    if (this.classList.contains('media-gallery--horizontal')) return false;

    return true;
  }

  /**
   * Handles variant selection from variant picker
   * Two modes: slide to variant image OR replace entire gallery
   * @param {Event} event - Variant update event
   */
  onVariantSelected(event) {
    if (!this.isConnected) return;

    const isStickyAddToCart = event.detail?.data?.isStickyAddToCart;
    const variantPicker = event.target;
    const selectedImageId = event.detail?.resource?.featured_media?.id?.toString() || variantPicker?.selectedOption?.dataset?.optionMediaId;
    if (this.#shouldSlideToVariantMedia(selectedImageId)) {
      this.slideToMediaById(selectedImageId);
    } else {
      const newMediaGallery = event.detail.data.html.querySelector('media-gallery');
      if (newMediaGallery) {
        this.cleanupZoom();
        this.classList.add('media-gallery-loaded');

        this.#appendModelViewerAssets(event.detail.data.html);
        morph(this, newMediaGallery);
        this.#initialize();
        if( isStickyAddToCart || isMobileBreakpoint() || this.isCarousel) return;
        const isSticky = this.classList.contains('sticky-content-active');
        const productDetails = this.closest('.product-information')?.querySelector('.product-information__details');
        const isInDialog = this.closest('.dialog');
        // #scrollIntoViewWithOffset only scrolls when user has scrolled past the gallery → always scrolling up
        const stickyOffset = isInDialog ? 0 : getStickyOffset({ scrollingUp: true });

        if (isSticky && productDetails && !isInDialog) {
          const galleryHeight = this.offsetHeight;
          const detailsHeight = productDetails.offsetHeight;
          // Only scroll if gallery height is greater than details height
          if (galleryHeight >= detailsHeight) {
            requestAnimationFrame(() => {
              this.#scrollIntoViewWithOffset(stickyOffset);
            });
          }
        } else {
          this.#scrollIntoViewWithOffset(stickyOffset);
        }
      }
    }
  }

  /**
   * Scrolls the gallery into view with sticky offset
   * Only scrolls if user has scrolled past the top of the gallery
   * @param {number} stickyOffset - The sticky offset in pixels (0 if in dialog)
   */
  #scrollIntoViewWithOffset(stickyOffset) {
    const rect = this.getBoundingClientRect();
    const elementTop = rect.top + window.scrollY;
    const currentScrollY = window.scrollY;

    // Only scroll if user has scrolled past the top of the gallery
    const hasScrolledPast = currentScrollY > elementTop;

    if (!hasScrolledPast) {
      return;
    }

    if (stickyOffset === 0) {
      this.scrollIntoView({ behavior: 'auto', block: 'start' });
    } else {
      // Not in dialog, scroll window with sticky offset
      const scrollPosition = elementTop - stickyOffset - 30;
      window.scrollTo({ top: Math.max(0, scrollPosition), behavior: 'auto' });
    }
  }

  /**
   * Slides the gallery to a specific media item by its ID.
   * Finds the slide with matching mediaId and animates to it with smooth transition.
   * @param {string} mediaId - The media ID to slide to
   */
  slideToMediaById(mediaId) {
    const swiper = this.gallerySwiper?.swiper;
    if (!mediaId || !swiper) return;

    const slides = swiper.slides;
    const slideIndex = slides.findIndex((slide) => slide.querySelector(`[data-media-id="${mediaId}"]`));
    if (slideIndex < 0) return;

    swiper.slideTo(slideIndex, 300, true);
  }

  #handleGalleryScroll = () => {
    if (this.#isClickScrolling || this.#galleryScrollRaf) return;
    this.#galleryScrollRaf = requestAnimationFrame(() => {
      this.#galleryScrollRaf = 0;
      this.#updateActiveSlide();
    });
  };

  #teardownGalleryScrollObserver() {
    const scrollEl = this.#scrollContainer ?? window;
    scrollEl.removeEventListener('scroll', this.#handleGalleryScroll);
    if (this.#onClickScrollEnd) {
      clearTimeout(this.#clickScrollTimer);
      scrollEl.removeEventListener('scrollend', this.#onClickScrollEnd);
      this.#onClickScrollEnd = null;
      this.#isClickScrolling = false;
    }
    this.#scrollContainer = null;
    this.#scrollContainerPaddingTop = 0;
    cancelAnimationFrame(this.#galleryScrollRaf);
    this.#galleryScrollRaf = 0;
    this.#cachedSlides = null;
    this.#cachedThumbItems = null;
    this.#cachedThumbContainer = null;
    this.#currentActiveIndex = -1;
  }

  #setupGalleryScrollObserver() {
    this.#teardownGalleryScrollObserver();
    const gallery = this.gallerySwiper;
    if (!gallery) return;
    this.#cachedSlides = Array.from(gallery.querySelectorAll('.swiper-slide'));
    if (!this.#cachedSlides.length) return;
    this.#cachedThumbContainer = this.querySelector('.media-thumbnails__stacked');
    this.#cachedThumbItems = this.#cachedThumbContainer
      ? Array.from(this.#cachedThumbContainer.querySelectorAll('.media-thumbnails__item'))
      : [];
    this.#cachedThumbItems.forEach(item => item.classList.remove('is-active'));
    const dialogInner = this.closest('.dialog__inner');
    this.#scrollContainer = dialogInner ?? window;
    this.#scrollContainerPaddingTop = dialogInner
      ? parseFloat(getComputedStyle(dialogInner).paddingBlockStart) || 0
      : 0;
    this.#scrollContainer.addEventListener('scroll', this.#handleGalleryScroll, { passive: true });
    this.#updateActiveSlide();
  }

  #updateActiveSlide() {
    const slides = this.#cachedSlides;
    if (!slides?.length) return;

    const scrollEl = this.#scrollContainer;
    let threshold;
    if (scrollEl && scrollEl !== window) {
      const rect = /** @type {Element} */ (scrollEl).getBoundingClientRect();
      threshold = rect.top + rect.height / 2;
    } else {
      const stickyOffset = getStickyOffset();
      threshold = stickyOffset + (window.innerHeight - stickyOffset) / 2;
    }

    const currentIndex = this.#currentActiveIndex;
    let activeIndex = currentIndex >= 0 ? currentIndex : 0;

    if (slides[activeIndex]?.getBoundingClientRect().top > threshold) {
      while (activeIndex > 0 && slides[activeIndex].getBoundingClientRect().top > threshold) {
        activeIndex -= 1;
      }
    } else {
      while (activeIndex + 1 < slides.length && slides[activeIndex + 1].getBoundingClientRect().top <= threshold) {
        activeIndex += 1;
      }
    }

    if (activeIndex === currentIndex) return;
    this.#syncThumbnailToIndex(activeIndex);
  }

  #syncThumbnailToIndex(index, behavior = 'smooth') {
    const items = this.#cachedThumbItems;
    const container = this.#cachedThumbContainer;
    if (!items?.length || !container) return;

    if (this.#currentActiveIndex >= 0) {
      items[this.#currentActiveIndex]?.classList.remove('is-active');
      this.#cachedSlides?.[this.#currentActiveIndex]?.querySelector('deferred-media')?.pauseMedia?.();
    }
    const activeItem = /** @type {HTMLElement} */ (items[index]);
    activeItem?.classList.add('is-active');
    this.#currentActiveIndex = index;

    const isLightboxOpen = this.lightbox?.pswp && !this.lightbox.pswp.isClosed;
    const newMedia = this.#cachedSlides?.[index]?.querySelector('deferred-media');
    if (newMedia && !isLightboxOpen) {
      syncDeferredMedia(newMedia, { isActive: true, shouldAutoplay: this.isVideoAutoplay, resumeNonAutoplay: this.isVideoAutoplay });
    }

    if (activeItem) {
      const top = Math.max(0, activeItem.offsetTop - container.clientHeight / 2 + activeItem.clientHeight / 2);
      container.scrollTo({ top, behavior });
    }
  }

  /**
   * Gets the main gallery carousel element.
   * @returns {HTMLElement|null} The gallery carousel container
   */
  get gallerySwiper() {
    return this.#gallerySwiperEl ?? this.querySelector('.media-gallery__carousel');
  }

  /**
   * Gets the thumbnails carousel element.
   * @returns {HTMLElement|null} The thumbnails carousel container
   */
  get thumbnailsSwiper() {
    return this.#thumbnailsSwiperEl ?? this.querySelector('.media-thumbnails__carousel');
  }

  /**
   * Gets the variant picker element from the product information section.
   * @returns {HTMLElement|null} The variant picker component
   */
  get variantPicker() {
    return this.closest('.product-information')?.querySelector('variant-picker');
  }

  /**
   * Checks if the gallery should use slider layout on desktop.
   * @returns {boolean} True if using left_thumbnails or bottom_thumbnails layout
   */
  get isGallerySlider() {
    return this.galleryLayoutDesktop === 'left_thumbnails' ||
           this.galleryLayoutDesktop === 'bottom_thumbnails' ||
           this.galleryLayoutDesktop === 'horizontal';
  }

  get isStackedWithThumbnails() {
    return this.galleryLayoutDesktop === 'stacked_images_thumbnails';
  }

  /**
   * Gets the desktop gallery layout configuration.
   * @returns {string} The layout type (defaults to 'left_thumbnails')
   */
  get galleryLayoutDesktop() {
    return this.getAttribute('data-layout-desktop') || 'left_thumbnails';
  }

  /**
   * Gets the mobile thumbnails layout configuration.
   * @returns {string} The layout type (defaults to 'thumbnails')
   */
  get galleryLayoutMobile() {
    return this.getAttribute('data-layout-mobile') || 'thumbnails';
  }

  /**
   * Checks if video autoplay is enabled.
   * @returns {boolean} True if video autoplay is enabled
   */
  get isVideoAutoplay() {
    return this.getAttribute('data-video-autoplay') === 'true';
  }

  /**
   * Checks if the gallery is in carousel mode.
   * @returns {boolean} True if carousel mode is enabled
   */
  get isCarousel() {
    return this.getAttribute('data-carousel') === 'true';
  }

  get zoom() {
    return this.getAttribute('data-zoom') || 'lightbox';
  }

  get zoomMobile() {
    return this.getAttribute('data-zoom-mobile') || 'lightbox';
  }


  /**
   * Checks if only selected variant media should be shown.
   * @returns {boolean} True if only selected variant media is displayed
   */
  get selectedVariantMediaOnly() {
    return this.getAttribute('data-selected-variant-media-only') === 'true';
  }

  /**
   * Checks if variant gallery should be shown.
   * @returns {boolean} True if variant gallery is displayed
   */
  get isVariantGallery() {
    return this.getAttribute('data-variant-gallery') === 'true';
  }
}

// Register the custom element if it hasn't been registered already
if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
